import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Loader2, AlertTriangle, RefreshCw, LogIn } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loader">
      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

/**
 * Shown when the session could not be confirmed for a NON-auth reason (offline,
 * CORS, 5xx). The token is still in this tab, so the admin is offered a retry
 * instead of being silently signed out / sent to the login page.
 */
function SessionUnverified({ message, hasToken, onRetry, onSignIn }) {
  return (
    <div className="min-h-[60vh] grid place-items-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-center" data-testid="admin-session-unverified">
        <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
        <h2 className="font-semibold text-slate-900 mt-3">Could not confirm your admin session</h2>
        <p className="text-sm text-slate-500 mt-1 break-words">
          {message || "The server could not be reached. Your session has NOT been cleared."}
        </p>
        <div className="flex items-center justify-center gap-2 mt-5">
          <button onClick={onRetry} data-testid="admin-session-retry"
            className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold text-sm px-4 py-2.5 rounded-lg">
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
          {hasToken && (
            <button onClick={onSignIn} data-testid="admin-session-signin"
              className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 font-semibold text-sm px-4 py-2.5 rounded-lg">
              <LogIn className="h-4 w-4" /> Sign in again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminRoute({ children }) {
  const { adminUser, ready, hasToken, sessionExpired, verifyError, retryValidate } = useAdminAuth();
  const location = useLocation();
  if (!ready) return <FullLoader />;
  const invalid = !adminUser || adminUser.role !== "admin";
  if (!invalid) return children;
  // A failed/unreachable verification must not be reported as an expired session,
  // and must not throw away a token that may still be perfectly valid.
  if (hasToken && !sessionExpired) {
    return (
      <SessionUnverified
        message={verifyError}
        hasToken={hasToken}
        onRetry={retryValidate}
        onSignIn={() => { window.location.assign("/admin/login"); }}
      />
    );
  }
  return (
    <Navigate
      to="/admin/login"
      replace
      state={sessionExpired ? { reason: "expired", from: location.pathname } : undefined}
    />
  );
}
